import '../../client/index.js';
import { wrapErrorManager } from '../../utils/error-manager.js';
import { transformCmsMenusResponse, transformCmsBlocksResponse, transformCmsPagesResponse, transformCmsMasterdataContentResponse } from './transformers.js';
import { arrayUnique, flatToTree } from '../../utils/index.js';
import { getCatalogProducts } from '../products/index.js';
import { getCatalogCategories } from '../categories/index.js';
import defaultClient from 'axios';

const getMenus = async ({ client , controller , code  } = {})=>{
    const { data: menu  } = await getCmsMenus({
        client,
        controller,
        code
    });
    const productSkus = arrayUnique(menu?.filter(({ type , entityId  })=>type === 'product' && entityId && isNaN(entityId)).map(({ entityId  })=>entityId));
    const categoryIds = arrayUnique(menu?.filter(({ type , entityId  })=>type === 'category' && entityId && !isNaN(entityId)).map(({ entityId  })=>entityId));
    const cmsBlockIdentifiers = arrayUnique(menu.filter(({ type , entityId  })=>type === 'cms_block' && entityId).map(({ entityId  })=>entityId));
    const getProductUrlKeys = getCatalogProducts({
        client,
        controller,
        fields: {
            sku: true,
            attributes: true
        },
        where: {
            sku: {
                inq: productSkus
            }
        }
    });
    const getCategoryPaths = getCatalogCategories({
        client,
        controller,
        fields: {
            id: true,
            path: true
        },
        where: {
            id: {
                inq: categoryIds
            }
        }
    });
    const getCmsBlockContent = getCmsBlocks({
        client,
        controller,
        fields: {
            idx: true,
            content: true
        },
        where: {
            idx: {
                inq: cmsBlockIdentifiers
            }
        }
    });
    const [productUrlKeys, categoryPaths, cmsBlockContents] = await Promise.all([
        getProductUrlKeys,
        getCategoryPaths,
        getCmsBlockContent
    ]);
    const menuCompleteWithPath = menu.map((item)=>{
        let path;
        let urlKey;
        let content;
        if (item.type === 'cms_page') {
            item.url = `/${item.entityId}`;
        }
        if (item.type === 'cms_block') {
            content = cmsBlockContents.data.find(({ identifier  })=>item.entityId === identifier)?.content;
        } else if (item.type === 'product') {
            urlKey = productUrlKeys.data.find(({ sku  })=>item.entityId === sku)?.urlKey;
        } else if (item.type === 'category') {
            path = categoryPaths.data.find(({ id  })=>item.entityId === id)?.path;
        }
        return {
            ...item,
            path,
            urlKey,
            content
        };
    });
    return {
        data: flatToTree({
            list: menuCompleteWithPath,
            children: menuCompleteWithPath,
            level: 0
        })
    };
};
getMenus.key = 'getMenus';
const getCmsMenus = async ({ client , controller , code  } = {})=>await client.get(`V1/cms/menus`, {
        signal: controller?.signal,
        params: {
            filter: {
                where: {
                    code
                },
                limit: 100000,
                offset: 0,
                order: [
                    'position ASC'
                ],
                fields: {
                    node_id: true,
                    type: true,
                    parent_id: true,
                    level: true,
                    title: true,
                    menu_title: true,
                    target: true,
                    url: true,
                    object_id: true,
                    css_classes: true,
                    max_items_for_column: true,
                    identifier: true
                }
            }
        },
        transformResponse: [
            ...(client || defaultClient).defaults.transformResponse,
            wrapErrorManager(transformCmsMenusResponse)
        ]
    });
getCmsMenus.key = 'getCmsMenus';
const getCmsBlocks = async ({ client , controller , fields ={} , where ={} , order =[] , limit =1000000 , offset =0 , skip =0  } = {})=>await client.get(`V1/cms/blocks`, {
        signal: controller?.signal,
        params: {
            filter: {
                fields,
                where,
                limit,
                offset,
                order,
                skip
            }
        },
        transformResponse: [
            ...(client || defaultClient).defaults.transformResponse,
            wrapErrorManager(transformCmsBlocksResponse)
        ]
    });
getCmsBlocks.key = 'getCmsBlocks';
const getCmsPages = async ({ client , controller , fields ={} , where ={} , order =[] , limit =1000000 , offset =0 , skip =0  } = {})=>{
    return await client.get(`V1/cms/pages`, {
        signal: controller?.signal,
        params: {
            filter: {
                where,
                limit,
                offset,
                fields,
                order,
                skip
            }
        },
        transformResponse: [
            ...(client || defaultClient).defaults.transformResponse,
            wrapErrorManager(transformCmsPagesResponse)
        ]
    });
};
getCmsPages.key = 'getCmsPages';
const getCmsMasterdataContentCount = async ({ client , controller , where ={}  } = {})=>await client.get(`V1/cms/masterdata/content/count`, {
        signal: controller?.signal,
        params: {
            where
        }
    });
getCmsMasterdataContentCount.key = 'getCmsMasterdataContentCount';
const getCmsMasterdataContent = async ({ client , controller , fields ={} , where ={} , order =[] , limit =1000000 , offset =0 , skip =0  } = {})=>await client.get(`V1/cms/masterdata/content`, {
        signal: controller?.signal,
        params: {
            filter: {
                where,
                limit,
                offset,
                fields,
                order,
                skip
            }
        },
        transformResponse: [
            ...(client || defaultClient).defaults.transformResponse,
            wrapErrorManager(transformCmsMasterdataContentResponse)
        ]
    });
getCmsMasterdataContent.key = 'getCmsMasterdataContent';
const getDynamicAreas = async ({ client , dynamicAreasDisplayEntity , dynamicAreasId  })=>getCmsBlocks({
        client,
        order: [
            'display_sort_order ASC'
        ],
        where: {
            and: [
                {
                    display_entity: dynamicAreasDisplayEntity
                },
                {
                    or: [
                        {
                            and: [
                                {
                                    display_selection_type: 'all'
                                },
                                {
                                    display_selection_target: ''
                                }
                            ]
                        },
                        {
                            and: [
                                {
                                    display_selection_type: 'specific'
                                },
                                {
                                    display_selection_target: dynamicAreasId?.toString()
                                }
                            ]
                        }
                    ]
                }
            ]
        }
    });

export { getCmsBlocks, getCmsMasterdataContent, getCmsMasterdataContentCount, getCmsMenus, getCmsPages, getDynamicAreas, getMenus };
